
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEOMetadata from '@/components/SEOMetadata';
import ResidentialRDPPricingSection from '@/components/ResidentialRDPPricingSection';
import { Monitor, Shield, Clock, ArrowRight, Globe, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

const ResidentialRemoteDesktops = () => {
  const benefits = [
    {
      icon: Globe,
      title: "US Residential IPs",
      description: "Authentic residential IP addresses for seamless access"
    },
    {
      icon: Shield,
      title: "Full Admin Access",
      description: "Complete control over your remote desktop environment"
    },
    {
      icon: Clock,
      title: "24/7 Support",
      description: "Round-the-clock technical assistance"
    }
  ];

  const scrollToPackages = () => {
    const packagesSection = document.querySelector('.rdp-packages-section');
    if (packagesSection) {
      const headerHeight = 80;
      const offset = packagesSection.getBoundingClientRect().top + window.pageYOffset - headerHeight;
      window.scrollTo({ 
        top: offset, 
        behavior: 'smooth' 
      });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOMetadata 
        title="Residential Remote Desktops - US IP Windows RDP | NEXX"
        description="Access powerful Windows remote desktops with authentic US residential IP addresses. Perfect for developers, marketers, and professionals. 24/7 support, full admin access."
        keywords="residential remote desktop, US IP RDP, Windows remote desktop, residential IP, remote development, ad verification, social media management"
        ogTitle="Residential Remote Desktops with US Residential IPs"
        ogDescription="Secure Windows remote desktops with authentic US residential IP addresses. Ideal for remote work, development, and marketing."
        canonical="https://nexx.net/residential-remote-desktops"
      />
      
      <Header />
      
      {/* Hero Section */}
      <section className="pt-20 pb-8 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
        {/* Floating Monitor Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-10">
          <div className="absolute top-16 left-12 opacity-5 animate-float-up" style={{ animationDuration: '8s' }}>
            <Monitor className="w-8 h-8 text-nexx-green" />
          </div>
          <div className="absolute top-32 right-16 opacity-5 animate-float-up" style={{ animationDuration: '10s', animationDelay: '2s' }}>
            <Monitor className="w-12 h-12 text-blue-500" />
          </div>
          <div className="absolute top-48 left-1/4 opacity-8 animate-float-up" style={{ animationDuration: '12s', animationDelay: '4s' }}>
            <Monitor className="w-6 h-6 text-gray-400" />
          </div>
          <div className="absolute top-60 right-1/3 opacity-5 animate-float-up" style={{ animationDuration: '9s', animationDelay: '1s' }}>
            <Monitor className="w-10 h-10 text-nexx-green" />
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-20">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6 mt-8">
              Residential <span className="text-nexx-green">Remote Desktops</span>
            </h1>
            <div className="text-2xl lg:text-3xl text-gray-600 mb-6 overflow-hidden relative">
              <span className="inline-block animate-grow-scale bg-gradient-to-r from-nexx-green to-blue-500 bg-clip-text text-transparent font-semibold">
                US-based workstations with residential IP addresses.
              </span>
            </div>
            <p className="text-lg text-gray-500 mb-4 max-w-3xl mx-auto">
              Access{' '}
              <span className="relative inline-block">
                <span className="font-bold text-gray-900 relative z-10">powerful Windows desktops</span>
                <span className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-blue-400 to-purple-500 transform scale-x-0 animate-[grow-width_1.5s_ease-out_0.5s_forwards]"></span>
              </span>{' '}
              from{' '}
              <span className="relative inline-block text-nexx-green font-bold">
                authentic US residential IP addresses
                <span className="absolute -inset-1 bg-green-100 rounded-lg opacity-30 animate-pulse"></span>
              </span>.
              Perfect for{' '}
              <span className="relative inline-block font-semibold text-blue-600">
                remote developers
                <span className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-400 animate-[grow-width_1s_ease-out_1s_forwards]"></span>
              </span>,{' '}
              <span className="relative inline-block font-semibold text-purple-600">
                marketers doing ad verification
                <span className="absolute bottom-0 left-0 w-full h-0.5 bg-purple-400 animate-[grow-width_1s_ease-out_1.5s_forwards]"></span>
              </span>, and{' '}
              <span className="relative inline-block font-semibold text-orange-600">
                professionals managing multiple accounts
                <span className="absolute bottom-0 left-0 w-full h-0.5 bg-orange-400 animate-[grow-width_1s_ease-out_2s_forwards]"></span>
              </span>{' '}
              while maintaining a{' '}
              <span className="relative inline-block">
                <span className="font-bold bg-gradient-to-r from-nexx-green to-green-600 bg-clip-text text-transparent">
                  clean digital footprint
                </span>
                <span className="absolute -bottom-1 left-0 w-full h-2 bg-gradient-to-r from-green-200 to-green-300 opacity-50 transform skew-x-12 animate-[grow-width_2s_ease-out_2.5s_forwards] scale-x-0"></span>
              </span>.
            </p>
            <p className="text-lg text-gray-500 mb-10 max-w-3xl mx-auto">
              <span className="inline-block animate-fade-in" style={{ animationDelay: '3s', opacity: 0 }}>
                💼 Ideal for{' '}
                <span className="relative inline-block font-bold text-blue-700">
                  international travelers
                  <span className="absolute -top-1 -right-1">
                    <span className="animate-ping absolute inline-flex h-3 w-3 rounded-full bg-blue-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-blue-500"></span>
                  </span>
                </span>{' '}
                who need a{' '}
                <span className="relative inline-block font-bold text-nexx-green">
                  centralized US location
                  <span className="absolute inset-0 bg-green-100 rounded-md opacity-20 animate-pulse"></span>
                </span>{' '}
                for all their{' '}
                <span className="font-semibold text-red-600 relative">
                  critical logins
                  <span className="absolute -inset-1">
                    <span className="absolute inset-0 bg-red-100 rounded animate-ping opacity-30"></span>
                  </span>
                </span>{' '}
                and work applications.
              </span>
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
              <Button 
                size="lg" 
                className="bg-nexx-green hover:bg-green-700 text-white px-8 py-4 text-lg group"
                data-tally-open="3NQKvW" 
                data-tally-emoji-text="👋" 
                data-tally-emoji-animation="wave"
              >
                Get Trial
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-nexx-green text-nexx-green hover:bg-nexx-green hover:text-white px-8 py-4 text-lg"
                onClick={scrollToPackages}
              >
                Order Now
              </Button>
              <Link to="/server-tos">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white px-8 py-4 text-lg"
                >
                  Read Our TOS
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Perfect For</h2>
            <p className="text-xl text-gray-600">Multiple use cases requiring authentic US residential presence</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            <div className="text-center group">
              <div className="w-16 h-16 bg-blue-50 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:bg-nexx-green group-hover:scale-110 transition-all duration-300">
                <Monitor className="h-8 w-8 text-blue-600 group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Remote Developers</h3>
              <p className="text-sm text-gray-600">US workbench for seamless development and testing</p>
            </div>
            
            <div className="text-center group">
              <div className="w-16 h-16 bg-green-50 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:bg-nexx-green group-hover:scale-110 transition-all duration-300">
                <Shield className="h-8 w-8 text-green-600 group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Ad Verification</h3>
              <p className="text-sm text-gray-600">Marketers verifying ads in US territory with minimal footprint</p>
            </div>
            
            <div className="text-center group">
              <div className="w-16 h-16 bg-purple-50 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:bg-nexx-green group-hover:scale-110 transition-all duration-300">
                <Users className="h-8 w-8 text-purple-600 group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Social Media Management</h3>
              <p className="text-sm text-gray-600">Manage multiple accounts without IP flagging concerns</p>
            </div>
            
            <div className="text-center group">
              <div className="w-16 h-16 bg-orange-50 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:bg-nexx-green group-hover:scale-110 transition-all duration-300">
                <Globe className="h-8 w-8 text-orange-600 group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">International Travelers</h3>
              <p className="text-sm text-gray-600">Central US location for all critical logins and applications</p>
            </div>
          </div>

          {/* Benefits Section */}
          <div className="grid md:grid-cols-3 gap-6 max-w-3xl mx-auto">
            {benefits.map((benefit, index) => (
              <div key={benefit.title} className="text-center group">
                <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:bg-nexx-green group-hover:scale-110 transition-all duration-300">
                  <benefit.icon className="h-6 w-6 text-nexx-green group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="text-base font-semibold text-gray-900 mb-1">{benefit.title}</h3>
                <p className="text-sm text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RDP Pricing Section */}
      <div className="rdp-packages-section">
        <ResidentialRDPPricingSection />
      </div>

      <Footer />
    </div>
  );
};

export default ResidentialRemoteDesktops;
